import React, {useMemo, useCallback, useEffect, useState} from "react";
import Forms from "../../../bricks/form/Forms";
import {axiosInstance} from "../../../bricks/axios";
import {API} from "../../../config/Config";
import {useModal} from "../../../context/ModalContext";
import {useAppData} from "../../../context/AppDataContext";
import TestQuestionAnswersModal from "./TestQuestionAnswersModal";

import "./TestResultModalContent.scss";

const EMPTY_OPTION = {label: "— nevyplněno —", value: null};

const getPointOptions = (max = 0) => [
    EMPTY_OPTION,
    ...Array.from({length: max + 1}, (_, i) => ({label: i, value: i}))
];

/**
 * @param {Object} props
 * @param {{_id?:string,testIndex:number,result?:number}[]} props.testResultList
 * @param {{[index:number]: number}} props.testPointsMap
 * @param {{order:number,min:number|null,max:number|null}[]} props.testDefinitionList
 * @param {(userId:string, trIdOrNull:string|null, testIndex:number, newPointsOrNull:number|null)=>Promise<Object|void>} props.onChangePoints
 * @param {string} props.userId
 * @param {string} [props.courseCode]
 */
const TestResultModalContent = ({
                                    testResultList = [],
                                    testPointsMap = {},
                                    testDefinitionList = [],
                                    onChangePoints,
                                    userId,
                                    courseCode,
                                    testType = 'sortingTest'
                                }) => {
    const [courses, setCourses] = useState([]);
    const [localResults, setLocalResults] = useState(testResultList);
    const {selectedPeriod, getSortingTestQuestionsList, setError} = useAppData();
    const [testQuestions, setTestQuestions] = useState([]);
    const [sortingTestQuestionResultList, setSortingTestQuestionResultList] = useState([]);
    const {openModal} = useModal();

    // načti definice otázek (jednorázově pro period)
    useEffect(() => {
        const loadTestQuestions = async () => {
            const data = await getSortingTestQuestionsList();
            setTestQuestions(data || []);
        };
        if (testType === 'sortingTest') {
            loadTestQuestions();
        } else {
            const loadOtherTestQuestions = async () => {
                try {
                    const {method, url} = API.loadOtherTestQuestionsById(testDefinitionList[0]._id);
                    const {data} = await axiosInstance[method](url);
                    setTestQuestions(data || []);
                } catch (e) {
                    setError(e);
                }
            };
            loadOtherTestQuestions()
        }
    }, [getSortingTestQuestionsList]);

    // drž lokální kopii výsledků synchronně s props
    useEffect(() => {
        setLocalResults(testResultList);
    }, [testResultList]);

    // definice testů do mapy
    const defsByOrder = useMemo(() => {
        const m = new Map();
        for (const d of testDefinitionList || []) m.set(Number(d.order), d);
        return m;
    }, [testDefinitionList]);

    // připrav všechny testy dle mapy max bodů
    const rows = useMemo(() => {
        const byIndex = new Map(localResults.map(tr => [tr.testIndex, tr]));
        return Object.entries(testPointsMap)
            .map(([indexStr, maxPts]) => {
                const testIndex = Number(indexStr);
                const tr = byIndex.get(testIndex);
                return {
                    _id: tr?._id ?? null,
                    testIndex,
                    maxPoints: Number(maxPts) || 0,
                    result: typeof tr?.result === "number" ? tr.result : null
                };
            })
            .sort((a, b) => a.testIndex - b.testIndex);
    }, [localResults, testPointsMap]);

    // kumulativní součet bodů před daným testem
    const cumulativeBefore = useMemo(() => {
        let sum = 0;
        const map = new Map();
        for (const r of rows) {
            map.set(r.testIndex, sum);
            sum += typeof r.result === "number" ? r.result : 0;
        }
        return map;
    }, [rows]);

    // povolenost testu dle definic a dosavadních výsledků
    const isEnabled = useCallback((testIndex, sourceRows = rows) => {
        const def = defsByOrder.get(testIndex);
        if (!def) return true;
        let sum = 0;
        for (const r of sourceRows) {
            if (r.testIndex >= testIndex) break;
            sum += typeof r.result === "number" ? r.result : 0;
        }
        if (def.min != null && sum < def.min) return false;
        if (def.max != null && sum > def.max) return false;
        return true;
    }, [defsByOrder, rows]);

    // existuje povolený test bez výsledku?
    const hasEnabledUnfinishedTest = useMemo(() => {
        return rows.some(r => isEnabled(r.testIndex) && (r.result == null));
    }, [rows, isEnabled]);

    // --- LOADERY ---

    // 1) Načíst user's testQuestionResults (kvůli času testu / odpovědím). Načítá se vždy (nezávisle na kurzech).
    const loadUserTestQuestionResults = useCallback(async () => {
        if (!userId || !selectedPeriod) return;
        try {
            let data;
            if (testType === 'sortingTest') {
                const {method, url} = API.getTestQuestionResultByPeriodAndUser(selectedPeriod, userId);
                const respone = await axiosInstance[method](url);
                data = respone.data;
            } else {
                const {method, url} = API.getTestQuestionResultByUserAndTestId(userId, testDefinitionList[0]._id);
                const respone = await axiosInstance[method](url);
                data = respone.data;
            }
            console.log(data);
            setSortingTestQuestionResultList(data || []);
        } catch (e) {
            console.error("Nepodařilo se načíst výsledky testů uživatele", e);
            setSortingTestQuestionResultList([]);
        }
    }, [userId, selectedPeriod]);

    useEffect(() => {
        loadUserTestQuestionResults();
    }, [loadUserTestQuestionResults]);

    // 2) Načíst dostupné kurzy – jen pokud uživatel NEMÁ další možný test k vyplnění
    const loadCourses = useCallback(async () => {
        if (!userId) return;
        try {
            const {method, url} = API.loadUsersAvailableCourses(userId);
            const {data} = await axiosInstance[method](url);
            setCourses(data ?? []);
        } catch (e) {
            console.error("Nepodařilo se načíst kurzy", e);
            setCourses([]);
        }
    }, [userId]);

    // Reaguj na změnu hasEnabledUnfinishedTest:
    // - pokud už není co vyplňovat => načti kurzy
    // - jinak kurzy vyčisti (a nenačítej)
    useEffect(() => {
        if (testType === 'otherTest') return;

        if (!userId) return;
        if (hasEnabledUnfinishedTest) {
            setCourses([]); // nic nezobrazovat a nezatěžovat API
        } else {
            loadCourses();
        }
    }, [userId, hasEnabledUnfinishedTest, loadCourses]);

    // změna bodů v testu
    const handleChange = useCallback(async (r, value) => {
        // 1) optimisticky uprav lokální stav
        setLocalResults(prev => {
            const next = [...prev];
            const idx = next.findIndex(x => x.testIndex === r.testIndex);
            if (idx >= 0) {
                next[idx] = {...next[idx], result: value === null ? undefined : value};
            } else {
                next.push({
                    _id: r._id ?? undefined,
                    testIndex: r.testIndex,
                    result: value === null ? undefined : value
                });
            }
            return next;
        });

        // 2) ulož na server
        const saved = await onChangePoints(userId, r._id, r.testIndex, value);
        if (!r._id && saved?._id) {
            setLocalResults(prev =>
                prev.map(item =>
                    item.testIndex === r.testIndex
                        ? {
                            ...item,
                            _id: saved._id,
                            result: typeof saved.result === "number" ? saved.result : item.result
                        }
                        : item
                )
            );
        }

        // 3) kaskáda – smaž následující testy, které už nově nejsou povolené
        const workRows = rows.map(x => ({...x}));
        const pos = workRows.findIndex(x => x.testIndex === r.testIndex);
        if (pos >= 0) workRows[pos].result = (value === null ? null : value);

        let cumulative = 0;
        for (const w of workRows) {
            if (w.testIndex < r.testIndex) {
                cumulative += typeof w.result === "number" ? w.result : 0;
            }
        }
        for (const w of workRows) {
            if (w.testIndex <= r.testIndex) continue;
            const def = defsByOrder.get(w.testIndex);
            const minOk = def?.min == null || cumulative >= def.min;
            const maxOk = def?.max == null || cumulative <= def.max;
            const allowed = minOk && maxOk;

            if (!allowed && w._id) {
                await onChangePoints(userId, w._id, w.testIndex, null);
                setLocalResults(prev => prev.map(item =>
                    item.testIndex === w.testIndex ? {...item, result: undefined} : item
                ));
                w.result = null;
            }
            cumulative += typeof w.result === "number" ? w.result : 0;
        }

        // 4) kurzy nenačítáme rovnou – řeší to efekt závislý na hasEnabledUnfinishedTest
        //    (po změně výsledků se rows/hasEnabledUnfinishedTest přepočítá a efekt případně kurzy načte/smaže)
    }, [onChangePoints, rows, defsByOrder, userId]);

    if (!rows.length) return <div>V aplikaci nejsou definovány žádné testy.</div>;

    const handleOpenTestQuestionResultModal = (testIndex) => {
        // otázky pro daný testIndex
        const questionsForTest = testQuestions.filter(q => Number(q.test) === Number(testIndex));
        // odpovědi uživatele pro daný testIndex
        const answersDoc = sortingTestQuestionResultList.find(d => Number(d.testIndex) === Number(testIndex));
        const answerMap = answersDoc?.answerMap || {};

        openModal(
            TestQuestionAnswersModal,
            {testIndex, questions: questionsForTest, answerMap},
            {title: `Odpovědi – Test #${testIndex}`, size: "lg"}
        );
    };

    return (
        <div className="TestResultsModalContent">
            {rows.map(r => {
                const percent = typeof r.result === "number" && r.maxPoints > 0
                    ? Math.round((r.result / r.maxPoints) * 100)
                    : 0;

                const def = defsByOrder.get(r.testIndex);
                const prevSum = cumulativeBefore.get(r.testIndex) ?? 0;
                let enabled;
                if (testType === 'otherTest') {
                    enabled = false;
                } else {
                    enabled = isEnabled(r.testIndex);
                }


                // najdi dokument s odpověďmi kvůli času testu
                const answersDoc = sortingTestQuestionResultList.find(d => Number(d.testIndex) === Number(r.testIndex));

                let testDuration;
                if (answersDoc?.start && answersDoc?.end) {
                    const start = new Date(answersDoc.start);
                    const end = new Date(answersDoc.end);
                    const diff = end.getTime() - start.getTime();
                    if (diff > 0) testDuration = Math.round(diff / 1000 / 60);
                }

                return (
                    <div key={`${r.testIndex}-${r._id ?? "new"}`} className="testRow">
                        <div className="name">
                            Test #{r.testIndex} {typeof testDuration === "number" ? `(${testDuration} min)` : ""}
                            <Forms.MagnificationIcon onClick={() => handleOpenTestQuestionResultModal(r.testIndex)}
                                                     size={18}/>
                        </div>

                        <div className="limits">
                            <small>
                                Hranice: {def?.min != null ? `min ${def.min}` : "min —"} / {def?.max != null ? `max ${def.max}` : "max —"}
                                <br/>
                                součet předchozích: {prevSum}
                            </small>
                        </div>

                        <div className="percent">
                            <progress
                                max={100}
                                value={typeof r.result === "number" ? (r.result / r.maxPoints) * 100 : 0}
                                style={{inlineSize: 90}}
                            />
                            &nbsp;{typeof r.result === "number" ? `${percent}%` : "—"}
                        </div>

                        <div className="points">
                            {typeof r.result === "number" ? r.result : "—"}/{r.maxPoints ?? "—"}
                        </div>

                        <div className="editor">
                            <Forms.Select
                                options={getPointOptions(r.maxPoints ?? 0)}
                                value={typeof r.result === "number" ? r.result : null}
                                onChange={(_, value) => handleChange(r, value)}
                                name={`tr_${r._id ?? `new_${r.testIndex}`}`}
                                label={false}
                                disabled={!enabled}
                            />
                        </div>
                    </div>
                );
            })}

            <div className="testBlock"/>

            {testType !== 'otherTest' && <div className="coursesBlock">
                {courseCode && (
                    <label className="courseInfo">
                        Uživatel je zapsaný v kurzu:&nbsp;<h5>{courseCode}</h5>
                    </label>
                )}

                {hasEnabledUnfinishedTest ? (
                    <div className="incompleteNotice">
                        Uživatel nemá dokončené testy, dostupné kurzy se načtou v případě,
                        že uživatel nemá žádný možný test k vyplnění.
                    </div>
                ) : (
                    <div>
                        <h4>Dostupné kurzy uživatele</h4>
                        {courses?.length ? (
                            <ul className="courseList">
                                {courses.map((c, i) => (
                                    <li key={c._id ?? `code_${i}`}>{c.code}</li>
                                ))}
                            </ul>
                        ) : (
                            <div>Uživatel nemá k dispozici žádné kurzy</div>
                        )}
                    </div>
                )}
            </div>}
        </div>
    );
};

export default TestResultModalContent;
